// ─── Export Builder Transform Logic ─────────────────────────────────
// Part 3: Address cleaning, date formatting, trim transforms
// Part 4: Calculated columns - concat, math, conditional_text
// Part 5: Margin rule resolver - percentage/fixed uplift, rounding, targeting
// Part 6: Invoice assignment - grouping, sequential numbering, derived fields
// All transforms are pure functions operating on string values.


import type {
  AddressAliasRule,
  ExportJobRow,
  ExportTemplateColumn,
  CalculatedConfig,
  ConcatPart,
  MathInput,
  MathOperator,
  ConditionalOperator,
  TransformType,
  MarginRule,
  MarginResult,
  RoundingMode,
  TargetScope,
  InvoiceRunSettings,
  InvoiceAssignment,
  InvoiceRunSummary,
  InvoiceGroupingMode,
  TownClassification,
  TownDisplayMode,
  DateColumnConfig,
  GstOutputMode,
  GstInputMode,
  BlankColumnConfig,
  SplitSelectConfig,
} from './types';


import {
  isMarginField,
  getMarginRuleId,
  isCalcColField,
  getCalcColId,
} from './types';


// ─── Resolved Calc Columns Helper ───────────────────────────────────

/**
 * Look up a field value from either the raw row data or the resolved
 * calculated columns map.
 *
 * When a field reference starts with `__calc_col__::`, it is a reference
 * to another calculated template column. We look up its already-resolved
 * value from the `resolvedCalcColumns` map (keyed by column ID).
 *
 * Otherwise, we fall back to the raw row data.
 */
function getFieldValue(
  field: string,
  row: ExportJobRow,
  resolvedCalcColumns?: Map<string, string>
): any {
  if (isCalcColField(field)) {
    const colId = getCalcColId(field);
    if (resolvedCalcColumns && resolvedCalcColumns.has(colId)) {
      return resolvedCalcColumns.get(colId) ?? '';
    }
    // Fallback: the referenced column hasn't been resolved yet
    return '';
  }
  return (row as any)[field];
}








// ─── Street Suffix Shortening Map ───────────────────────────────────

const STREET_SUFFIXES: Record<string, string> = {
  'Street': 'St',
  'street': 'St',
  'Road': 'Rd',
  'road': 'Rd',
  'Avenue': 'Ave',
  'avenue': 'Ave',
  'Terrace': 'Tce',
  'terrace': 'Tce',
  'Place': 'Pl',
  'place': 'Pl',
  'Crescent': 'Cres',
  'crescent': 'Cres',
  'Drive': 'Dr',
  'drive': 'Dr',
  'Lane': 'Ln',
  'lane': 'Ln',
  'Boulevard': 'Blvd',
  'boulevard': 'Blvd',
  'Court': 'Ct',
  'court': 'Ct',
  'Highway': 'Hwy',
  'highway': 'Hwy',
  'Parade': 'Pde',
  'parade': 'Pde',
  'Grove': 'Gr',
  'grove': 'Gr',
  'Close': 'Cl',
  'close': 'Cl',
};

// Build a regex for suffix replacement (word boundary match)
const suffixPattern = new RegExp(
  '\\b(' + Object.keys(STREET_SUFFIXES).join('|') + ')\\b',
  'g'
);

// ─── Alias Rule Matching ────────────────────────────────────────────

/**
 * Try to match a raw address against alias rules.
 * Returns the replacement value of the first matching active rule, or null if no match.
 */
export function applyAliasRules(
  raw: string,
  rules: AddressAliasRule[],
  fieldType: 'pickup' | 'dropoff'
): string | null {
  if (!raw || !rules || rules.length === 0) return null;

  for (const rule of rules) {
    if (!rule.is_active) continue;
    if (rule.applies_to !== 'both' && rule.applies_to !== fieldType) continue;

    let matched = false;
    switch (rule.match_type) {
      case 'exact':
        matched = raw.toLowerCase() === rule.raw_pattern.toLowerCase();
        break;
      case 'starts_with':
        matched = raw.toLowerCase().startsWith(rule.raw_pattern.toLowerCase());
        break;
      case 'contains':
        matched = raw.toLowerCase().includes(rule.raw_pattern.toLowerCase());
        break;
      case 'regex':
        try {
          const re = new RegExp(rule.raw_pattern, 'i');
          matched = re.test(raw);
        } catch {
          matched = false;
        }
        break;
    }

    if (matched) {
      return rule.replacement_value;
    }
  }

  return null;
}

// ─── Country Name Pattern ───────────────────────────────────────────
const COUNTRY_PATTERN = /^(new\s*zealand|nz|australia|au)$/i;

// ─── Standard Address Cleaner ───────────────────────────────────────

/**
 * Clean an address string using standard rules:
 * 1. Remove anything inside parentheses (including the parens)
 * 2. Split by comma — first part is the street
 * 3. If town classifications are provided, check remaining parts for classified "towns"
 *    and append the town name to preserve out-of-area context (e.g. Marton, Feilding)
 * 4. Trim whitespace / normalize
 * 5. Shorten common street suffixes
 * 6. Conservative house number removal
 */
export function cleanAddressStandard(
  raw: string,
  townClassifications?: Map<string, TownClassification>
): string {
  if (!raw) return '';

  let cleaned = raw;

  // Step 1: Remove parenthesized content (e.g. "(411, PORT)")
  cleaned = cleaned.replace(/\s*\([^)]*\)/g, '');

  // Step 2: Split by comma and process parts
  const parts = cleaned.split(',').map(p => p.trim()).filter(Boolean);

  // First part is always the street address
  let streetPart = parts[0] || '';

  // Step 2b: Check remaining comma-separated parts for classified town names
  // If a part matches a town classification with location_type === 'town',
  // preserve it in the output so out-of-area trips are visible
  // (e.g. "130 Hendersons Line, Marton, New Zealand" → "Hendersons Line, Marton")
  let townSuffix = '';
  if (townClassifications && townClassifications.size > 0 && parts.length > 1) {
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i].trim();
      if (!part || COUNTRY_PATTERN.test(part)) continue; // Skip country names

      const key = part.toLowerCase();
      const classification = townClassifications.get(key);
      if (classification && classification.location_type === 'town') {
        // Use the display_as name if available, otherwise the raw part
        townSuffix = classification.display_as || part;
        break;
      }
    }
  }

  // Step 3 & 4: Trim and normalize whitespace on street part
  streetPart = streetPart.replace(/\s+/g, ' ').trim();

  // Step 5: Shorten street suffixes
  streetPart = streetPart.replace(suffixPattern, (match) => STREET_SUFFIXES[match] || match);

  // Step 6: Conservative house number removal
  const leadingNumberMatch = streetPart.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
  if (leadingNumberMatch) {
    const [, , rest] = leadingNumberMatch;
    const words = rest.split(/\s+/);
    const looksLikeStreet = words.length >= 1 &&
      /^[A-Z]/.test(words[0]) &&
      words.some(w => Object.keys(STREET_SUFFIXES).some(s => s.toLowerCase() === w.toLowerCase() || Object.values(STREET_SUFFIXES).some(v => v.toLowerCase() === w.toLowerCase())));

    if (looksLikeStreet) {
      streetPart = rest;
    }
  }

  streetPart = streetPart.trim();

  // Append town name if a classified town was found in the address parts
  if (townSuffix) {
    return `${streetPart} ${townSuffix}`;
  }

  return streetPart;

}

// ─── Full Address Clean (Alias + Standard) ──────────────────────────

/**
 * Clean an address using alias rules first, then standard cleaner as fallback.
 * When town classifications are provided, classified "town" names found in
 * comma-separated address parts are preserved in the output.
 */
export function cleanAddress(
  raw: string | null,
  rules: AddressAliasRule[],
  fieldType: 'pickup' | 'dropoff' = 'both' as any,
  townClassifications?: Map<string, TownClassification>
): string {
  if (!raw) return '';

  const aliasResult = applyAliasRules(raw, rules, fieldType);
  if (aliasResult !== null) {
    return aliasResult;
  }

  return cleanAddressStandard(raw, townClassifications);
}


// ─── Date Format Transform ──────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date string (expected YYYY-MM-DD from Supabase) into a display format.
 */
export function formatDate(raw: string | null, format: string): string {
  if (!raw) return '';

  const parts = raw.split('-');
  if (parts.length !== 3) return raw;

  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'D/M/YYYY':
      return `${dayNum}/${monthNum}/${year}`;
    case 'YYYY-MM-DD':
      return raw;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    case 'D MMM YYYY':
      return `${dayNum} ${MONTH_NAMES[monthNum - 1] || month} ${year}`;
    default:
      return raw;
  }
}

// ─── Trim Transform ─────────────────────────────────────────────────

export function trimValue(raw: string | null): string {
  if (!raw) return '';
  return String(raw).trim();
}

// ─── Town Format Transform ──────────────────────────────────────────

/**
 * Parse a TaxiCaller address to extract the town code.
 * Format: "POI, Street (ZoneCode, TownCode)"
 * Returns the town code (e.g. "PN", "West", "Mils", "PORT")
 */
function extractTownCode(raw: string): string {
  if (!raw) return '';

  // Find the last parenthetical group: (ZoneCode, TownCode)
  const lastParenMatch = raw.match(/\(([^()]+)\)\s*$/);
  if (lastParenMatch) {
    const insideParen = lastParenMatch[1];
    const parenParts = insideParen.split(',').map(p => p.trim());
    if (parenParts.length >= 2) {
      // Return the town code (everything after the zone number)
      return parenParts.slice(1).join(',').trim();
    } else if (parenParts.length === 1) {
      return parenParts[0];
    }
  }

  // Fallback: try old comma-split approach for standard addresses
  const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
  const filtered = parts.filter(p => !/^(new\s*zealand|nz)$/i.test(p));
  if (filtered.length > 1) {
    return filtered[filtered.length - 1]; // Last non-NZ part
  }
  return filtered[0] || '';
}

/**
 * Format an address to show the town/city based on town classifications.
 *
 * @param raw - The raw address string
 * @param mode - The display mode (town_only, suburb_and_city, smart)
 * @param classifications - Map of location_name (lowercase) -> TownClassification
 */
export function formatTown(
  raw: string | null,
  mode: TownDisplayMode,
  classifications: Map<string, TownClassification>
): string {
  if (!raw) return '';

  const townCode = extractTownCode(raw);
  if (!townCode) return '';

  const key = townCode.toLowerCase().trim();
  const classification = classifications.get(key);

  if (!classification) {
    // No classification found - return the town code as-is
    return townCode;
  }

  switch (mode) {
    case 'town_only':
      if (classification.location_type === 'suburb' && classification.parent_town) {
        return classification.parent_town;
      }
      return classification.display_as || townCode;

    case 'suburb_and_city':
      if (classification.location_type === 'suburb' && classification.parent_town) {
        return `${classification.display_as || townCode}, ${classification.parent_town}`;
      }
      return classification.display_as || townCode;

    case 'smart':
    default:
      if (classification.location_type === 'suburb' && classification.parent_town) {
        return classification.parent_town;
      }
      if (classification.location_type === 'town') {
        return classification.display_as || townCode;
      }
      return classification.display_as || townCode;
  }
}

// ─── Split & Select Transform ───────────────────────────────────────

/**
 * Apply the Split & Select transform to a raw value.
 *
 * Splits the value by the configured delimiter, then selects the specified parts
 * (by 0-indexed position) and joins them back together with the configured join separator.
 *
 * - If splitSelectAll is true, all parts are included.
 * - If a selected part index is beyond the number of available parts, it outputs blank.
 * - Selected parts are joined with splitSelectJoinWith (default "/").
 * - Each part can optionally be trimmed (default true).
 */
export function applySplitSelect(
  raw: string | null,
  config: any
): string {
  if (!raw) return '';
  const strVal = String(raw);

  const delimiter: string = config?.splitSelectDelimiter || '/';
  const selectAll: boolean = config?.splitSelectAll ?? true;
  const selectedParts: number[] = config?.splitSelectParts || [];
  const joinWith: string = config?.splitSelectJoinWith ?? '/';
  const trimParts: boolean = config?.splitSelectTrimParts !== false; // default true

  // Split the value
  const parts = strVal.split(delimiter);

  if (selectAll) {
    // Return all parts joined with the join separator
    const processed = parts.map(p => trimParts ? p.trim() : p).filter(p => p !== '');
    return processed.join(joinWith);
  }

  if (selectedParts.length === 0) {
    return strVal; // No parts selected, return original
  }

  // Select specific parts by index
  // If a part doesn't exist at that index, output blank (empty string)
  const result = selectedParts
    .sort((a, b) => a - b) // Maintain order
    .map(idx => {
      if (idx >= 0 && idx < parts.length) {
        const part = parts[idx];
        return trimParts ? part.trim() : part;
      }
      return ''; // Missing part = blank
    })
    .filter(p => p !== ''); // Skip blank parts when joining

  return result.join(joinWith);
}


// ─── Apply Transform to a Value ─────────────────────────────────────

/**
 * Apply the specified transform to a raw value.
 * Returns the transformed string.
 */
export function applyTransform(
  rawValue: any,
  transformType: string | null,
  transformConfig: any | null,
  aliasRules: AddressAliasRule[],
  sourceField?: string | null,
  townClassifications?: Map<string, TownClassification>
): string {
  const strVal = rawValue == null ? '' : String(rawValue);

  if (!transformType || transformType === 'none') {
    return strVal;
  }
  switch (transformType) {

    case 'clean_address': {
      let fieldType: 'pickup' | 'dropoff' = 'pickup';
      if (sourceField === 'drop_off_location') fieldType = 'dropoff';
      return cleanAddress(strVal || null, aliasRules, fieldType, townClassifications);
    }

    case 'date_format': {
      const fmt = transformConfig?.format || 'DD/MM/YYYY';
      return formatDate(strVal || null, fmt);
    }
    case 'trim':
      return trimValue(strVal || null);
    case 'town_format': {
      const mode: TownDisplayMode = transformConfig?.townDisplayMode || 'smart';
      if (townClassifications && townClassifications.size > 0) {
        return formatTown(strVal || null, mode, townClassifications);
      }
      // Fallback: extract town code from address
      const tc = extractTownCode(strVal);
      return tc || '';
    }

    case 'split_select':
      return applySplitSelect(strVal || null, transformConfig);

    default:
      return strVal;
  }
}



// ─── Apply Transform to a Field Part (for concat) ───────────────────

/**
 * Apply a transform to a field value within a concat part.
 * Supports the same transforms as applyTransform but uses part-level config.
 */
function applyPartTransform(
  rawValue: any,
  transform: TransformType | string | undefined,
  transformConfig: Record<string, any> | undefined,
  aliasRules: AddressAliasRule[],
  sourceField?: string,
  townClassifications?: Map<string, TownClassification>
): string {
  if (!transform || transform === 'none') {
    return rawValue == null ? '' : String(rawValue);
  }
  return applyTransform(rawValue, transform, transformConfig || null, aliasRules, sourceField, townClassifications);
}

// ─── Calculated Column Resolution ───────────────────────────────────

/**
 * Resolve a concat calculated column.
 * Builds a string from ordered parts, applying per-part transforms.
 * Suppresses leading/trailing separators when adjacent field is blank.
 *
 * When a field part references another calculated column (via __calc_col__:: prefix),
 * the value is looked up from the resolvedCalcColumns map.
 */
function resolveConcat(
  config: CalculatedConfig,
  row: ExportJobRow,
  aliasRules: AddressAliasRule[],
  townClassifications?: Map<string, TownClassification>,
  resolvedCalcColumns?: Map<string, string>
): string {
  const parts = config.parts || [];
  if (parts.length === 0) return '';

  // Resolve each part to its value
  const resolvedParts: { partType: string; value: string }[] = parts.map((part: ConcatPart) => {
    switch (part.partType) {
      case 'field': {
        if (!part.field) return { partType: 'field', value: '' };
        const rawValue = getFieldValue(part.field, row, resolvedCalcColumns);
        const transformed = applyPartTransform(rawValue, part.transform, part.transformConfig, aliasRules, part.field, townClassifications);
        return { partType: 'field', value: transformed };
      }
      case 'text':
        return { partType: 'text', value: part.value || '' };
      case 'separator':
        return { partType: 'separator', value: part.value || '' };
      default:
        return { partType: 'text', value: '' };
    }
  });

  // Smart concatenation: suppress separators/text adjacent to blank fields
  let result = '';
  for (let i = 0; i < resolvedParts.length; i++) {
    const current = resolvedParts[i];

    if (current.partType === 'separator' || current.partType === 'text') {
      // Check if this separator/text is between two fields and one is blank
      const prevField = findNearestField(resolvedParts, i, 'backward');
      const nextField = findNearestField(resolvedParts, i, 'forward');

      // If the previous field is blank or the next field is blank, skip this separator
      if (current.partType === 'separator') {
        if ((prevField !== null && resolvedParts[prevField].value === '') ||
            (nextField !== null && resolvedParts[nextField].value === '')) {
          continue;
        }
      }
      result += current.value;
    } else {
      result += current.value;
    }
  }

  // Clean up: trim and remove doubled spaces
  result = result.replace(/\s+/g, ' ').trim();

  // Remove leading/trailing separators that might remain
  for (const part of parts) {
    if (part.partType === 'separator' && part.value) {
      const sep = part.value.trim();
      if (sep && result.startsWith(sep)) result = result.substring(sep.length).trim();
      if (sep && result.endsWith(sep)) result = result.substring(0, result.length - sep.length).trim();
    }
  }

  return result.trim();
}


/** Helper: find nearest field part in a direction */
function findNearestField(parts: { partType: string; value: string }[], fromIdx: number, direction: 'forward' | 'backward'): number | null {
  const step = direction === 'forward' ? 1 : -1;
  let i = fromIdx + step;
  while (i >= 0 && i < parts.length) {
    if (parts[i].partType === 'field') return i;
    i += step;
  }
  return null;
}

/**
 * Get the numeric factor from a margin rule for use in math expressions.
 * - Percentage: returns the multiplier (e.g. 10% → 1.10, -5% → 0.95)
 * - Fixed amount: returns the raw margin_value (e.g. $5 → 5)
 */
export function getMarginFactor(rule: MarginRule): number {
  if (rule.margin_type === 'percentage') {
    return 1 + rule.margin_value / 100;
  }
  return rule.margin_value;
}

/**
 * Resolve a math calculated column.
 * Evaluates a left-to-right expression with 2-4 inputs.
 * Supports field, static, and margin input types.
 *
 * When a field input references another calculated column (via __calc_col__:: prefix),
 * the value is looked up from the resolvedCalcColumns map.
 */
function resolveMath(
  config: CalculatedConfig,
  row: ExportJobRow,
  marginRules: MarginRule[] = [],
  resolvedCalcColumns?: Map<string, string>
): string {
  const inputs = config.inputs || [];
  if (inputs.length === 0) return '';

  // Parse inputs into values and operators
  const values: number[] = [];
  const operators: MathOperator[] = [];

  // Track margin rules referenced so we can apply their rounding to the result
  const referencedMarginRules: MarginRule[] = [];

  for (const input of inputs) {
    if (input.operator) {
      operators.push(input.operator);
    } else if (input.inputType === 'field') {
      const fieldName = input.field || '';
      // Check if this field reference is a margin rule factor
      if (isMarginField(fieldName)) {
        const ruleId = getMarginRuleId(fieldName);
        const rule = marginRules.find(r => r.id === ruleId);
        if (rule) {
          values.push(getMarginFactor(rule));
          referencedMarginRules.push(rule);
        } else {
          values.push(1); // Fallback: no matching rule, use 1 (neutral for multiply)
        }
      } else {
        // Use getFieldValue to resolve calc col references
        const rawVal = getFieldValue(fieldName, row, resolvedCalcColumns);
        values.push(rawVal == null ? 0 : Number(rawVal) || 0);
      }
    } else if (input.inputType === 'static') {
      values.push(input.value ?? 0);
    } else if (input.inputType === 'margin') {
      // Look up the margin rule by ID and compute the factor
      const rule = marginRules.find(r => r.id === input.marginRuleId);
      if (rule) {
        values.push(getMarginFactor(rule));
        referencedMarginRules.push(rule);
      } else {
        // Fallback: no matching rule found, use 1 (no effect for multiply)
        values.push(1);
      }
    }
  }


  if (values.length === 0) return '';

  // Evaluate left to right
  let result = values[0];
  for (let i = 0; i < operators.length && i + 1 < values.length; i++) {
    const op = operators[i];
    const nextVal = values[i + 1];
    switch (op) {
      case 'add': result += nextVal; break;
      case 'subtract': result -= nextVal; break;
      case 'multiply': result *= nextVal; break;
      case 'divide': result = nextVal !== 0 ? result / nextVal : 0; break;
    }
  }

  // Apply rounding from referenced margin rules.
  // If any margin rule was used as an input, apply its rounding config
  // to the final math result. Use the last referenced rule's rounding.
  if (referencedMarginRules.length > 0 && Number.isFinite(result)) {
    const roundingRule = referencedMarginRules[referencedMarginRules.length - 1];
    if (roundingRule.rounding_mode && roundingRule.rounding_mode !== 'none' && roundingRule.rounding_unit > 0) {
      result = applyRounding(result, roundingRule.rounding_mode as RoundingMode, roundingRule.rounding_unit);
    }
  }

  // Return with reasonable decimal precision
  if (Number.isFinite(result)) {
    // If result is very close to an integer, return integer
    if (Math.abs(result - Math.round(result)) < 0.0001) {
      return String(Math.round(result * 100) / 100);
    }
    return String(Math.round(result * 100) / 100);
  }
  return '0';
}



/**
 * Resolve a conditional_text calculated column.
 * Evaluates a condition and returns thenValue or elseValue.
 *
 * When the condition field references another calculated column,
 * the value is looked up from the resolvedCalcColumns map.
 */
function resolveConditionalText(
  config: CalculatedConfig,
  row: ExportJobRow,
  resolvedCalcColumns?: Map<string, string>
): string {
  const condition = config.condition;
  if (!condition || !condition.field) return config.elseValue || '';

  const rawValue = getFieldValue(condition.field, row, resolvedCalcColumns);
  const op = condition.operator as ConditionalOperator;
  let conditionMet = false;

  switch (op) {
    case 'is_blank':
      conditionMet = rawValue == null || rawValue === '' || rawValue === undefined;
      break;
    case 'is_not_blank':
      conditionMet = rawValue != null && rawValue !== '' && rawValue !== undefined;
      break;
    case 'is_true':
      conditionMet = rawValue === true || rawValue === 'true' || rawValue === 1;
      break;
    case 'is_false':
      conditionMet = rawValue === false || rawValue === 'false' || rawValue === 0 || rawValue == null;
      break;
    case 'eq':
      conditionMet = String(rawValue) === String(condition.value);
      break;
    case 'neq':
      conditionMet = String(rawValue) !== String(condition.value);
      break;
    case 'gt':
      conditionMet = Number(rawValue || 0) > Number(condition.value || 0);
      break;
    case 'gte':
      conditionMet = Number(rawValue || 0) >= Number(condition.value || 0);
      break;
    case 'lt':
      conditionMet = Number(rawValue || 0) < Number(condition.value || 0);
      break;
    case 'lte':
      conditionMet = Number(rawValue || 0) <= Number(condition.value || 0);
      break;
    default:
      conditionMet = false;
  }

  return conditionMet ? (config.thenValue || '') : (config.elseValue || '');
}

/**
 * Resolve a split calculated column.
 * Splits a field value by a delimiter and returns the specified part.
 *
 * When the split field references another calculated column,
 * the value is looked up from the resolvedCalcColumns map.
 */
function resolveSplit(
  config: CalculatedConfig,
  row: ExportJobRow,
  resolvedCalcColumns?: Map<string, string>
): string {
  const field = config.splitField;
  const delimiter = config.splitDelimiter;
  const partIndex = config.splitPartIndex ?? 0;
  const trimResult = config.splitTrimResult !== false; // default true

  if (!field) return '';

  const rawValue = getFieldValue(field, row, resolvedCalcColumns);
  if (rawValue == null || rawValue === '') return '';

  const strVal = String(rawValue);

  if (!delimiter) return trimResult ? strVal.trim() : strVal;

  const parts = strVal.split(delimiter);

  if (partIndex < 0 || partIndex >= parts.length) {
    // If the requested part doesn't exist, return empty
    return '';
  }

  const result = parts[partIndex];
  return trimResult ? result.trim() : result;
}

/**
 * Resolve a GST calculated column.
 * Calculates GST-inclusive, GST-exclusive, or GST amount from a source field.
 *
 * NZ GST default is 15%.
 * If input is GST-inclusive (default for taxi fares):
 *   exclusive = inclusive / (1 + rate/100)
 *   gst_only  = inclusive - exclusive
 * If input is GST-exclusive:
 *   inclusive = exclusive * (1 + rate/100)
 *   gst_only  = exclusive * (rate/100)
 *
 * When the source field references another calculated column (via __calc_col__:: prefix),
 * the value is looked up from the resolvedCalcColumns map.
 */
function resolveGst(
  config: CalculatedConfig,
  row: ExportJobRow,
  resolvedCalcColumns?: Map<string, string>
): string {
  const field = config.gstSourceField;
  if (!field) return '';

  // Use getFieldValue to resolve calc col references
  const rawValue = getFieldValue(field, row, resolvedCalcColumns);
  if (rawValue == null || rawValue === '') return '';

  const sourceAmount = Number(rawValue) || 0;
  const rate = config.gstRate ?? 15;
  const inputMode: GstInputMode = config.gstInputMode || 'inclusive';
  const outputMode: GstOutputMode = config.gstOutputMode || 'exclusive';
  const decimals = config.gstDecimalPlaces ?? 2;

  let inclusive: number;
  let exclusive: number;
  let gstAmount: number;

  if (inputMode === 'inclusive') {
    inclusive = sourceAmount;
    exclusive = sourceAmount / (1 + rate / 100);
    gstAmount = inclusive - exclusive;
  } else {
    exclusive = sourceAmount;
    inclusive = sourceAmount * (1 + rate / 100);
    gstAmount = sourceAmount * (rate / 100);
  }

  let result: number;
  switch (outputMode) {
    case 'inclusive':
      result = inclusive;
      break;
    case 'exclusive':
      result = exclusive;
      break;
    case 'gst_only':
      result = gstAmount;
      break;
    default:
      result = sourceAmount;
  }

  if (!Number.isFinite(result)) return '0';
  return result.toFixed(decimals);
}

/**
 * Resolve a calculated column value.
 * Dispatches to the appropriate resolver based on calcType.
 * Accepts optional marginRules for math expressions that reference margin factors.
 * Accepts optional townClassifications for address cleaning in concat parts.
 * Accepts optional resolvedCalcColumns map for inter-column references.
 *
 * When a calculated column references another calculated column (e.g. GST on
 * a concat-built "Inc-GST Price"), the resolvedCalcColumns map provides the
 * already-resolved values of earlier columns so they can be used as inputs.
 */
export function resolveCalculatedValue(
  config: CalculatedConfig | null,
  row: ExportJobRow,
  aliasRules: AddressAliasRule[],
  marginRules: MarginRule[] = [],
  townClassifications?: Map<string, TownClassification>,
  resolvedCalcColumns?: Map<string, string>
): string {
  if (!config || !config.calcType) return '(no config)';

  switch (config.calcType) {
    case 'concat':
      return resolveConcat(config, row, aliasRules, townClassifications, resolvedCalcColumns);
    case 'math':
      return resolveMath(config, row, marginRules, resolvedCalcColumns);
    case 'conditional_text':
      return resolveConditionalText(config, row, resolvedCalcColumns);
    case 'split':
      return resolveSplit(config, row, resolvedCalcColumns);
    case 'gst':
      return resolveGst(config, row, resolvedCalcColumns);
    case 'excel_formula':
      return config.excelFormulaTemplate || '';
    default:
      return `(unknown: ${config.calcType})`;
  }
}








// ─── Date Column Resolution ─────────────────────────────────────────

/**
 * Resolve a date column value.
 * Supports "today" mode (current date at export time), "custom" mode (fixed
 * date), and "range_end" mode (the export's own Date To filter value, passed
 * in as rangeEndDate -- YYYY-MM-DD -- by the caller since a date column has
 * no access to the filter panel itself). Formats the date using the
 * configured format via the existing formatDate function.
 */
export function resolveDateColumn(config: DateColumnConfig | null, rangeEndDate?: string): string {
  if (!config) return '';

  const dateFormat = config.dateFormat || 'DD-MM-YYYY';

  let isoDate: string; // YYYY-MM-DD

  if (config.dateMode === 'custom' && config.customDate) {
    isoDate = config.customDate; // Already YYYY-MM-DD
  } else if (config.dateMode === 'range_end' && rangeEndDate) {
    isoDate = rangeEndDate;
  } else {
    // Default: today's date -- also the fallback for range_end when no
    // Date To filter is set, so the column never silently ends up blank.
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    isoDate = `${year}-${month}-${day}`;
  }

  return formatDate(isoDate, dateFormat);
}


// ─── Resolve Column Value for a Row ─────────────────────────────────

/**
 * Resolve the output value for a single template column against a single data row.
 * This is the main function used by the preview panel.
 */
export function resolveColumnValue(
  col: ExportTemplateColumn,
  row: ExportJobRow,
  aliasRules: AddressAliasRule[],
  townClassifications?: Map<string, TownClassification>,
  rangeEndDate?: string
): string {
  if (!col.is_enabled) return '';

  switch (col.source_type) {
    case 'source_field': {
      if (!col.source_field) return '';
      const rawValue = (row as any)[col.source_field];
      return applyTransform(rawValue, col.transform_type, col.transform_config, aliasRules, col.source_field, townClassifications);
    }
    case 'static_value':
      return col.static_value || '';
    case 'blank':
      return '';
    case 'manual_default':
      return col.default_value || '';
    case 'calculated':
      return resolveCalculatedValue(col.transform_config as CalculatedConfig | null, row, aliasRules, [], townClassifications);

    case 'date':
      return resolveDateColumn(col.transform_config as DateColumnConfig | null, rangeEndDate);
    default:
      return '';
  }
}


// ─── Generate Preview Rows ──────────────────────────────────────────

/**
 * Generate preview output for a set of rows using the template columns.
 * Returns an array of objects where keys are column IDs.
 */
export function generatePreviewRows(
  columns: ExportTemplateColumn[],
  rows: ExportJobRow[],
  aliasRules: AddressAliasRule[]
): Record<string, string>[] {
  const enabledCols = columns.filter(c => c.is_enabled).sort((a, b) => a.column_order - b.column_order);

  return rows.map(row => {
    const outputRow: Record<string, string> = {};
    for (const col of enabledCols) {
      outputRow[col.export_column_name] = resolveColumnValue(col, row, aliasRules);
    }
    return outputRow;
  });
}


// ═══════════════════════════════════════════════════════════════════════
// Part 5: Margin Rule Resolver
// ═══════════════════════════════════════════════════════════════════════

// ─── Rounding Logic ─────────────────────────────────────────────────

/**
 * Apply rounding to a value based on mode and unit.
 * - none: return as-is
 * - nearest: round to nearest unit
 * - up: always round up (ceiling) to unit
 * - down: always round down (floor) to unit
 */
export function applyRounding(value: number, mode: RoundingMode, unit: number): number {
  if (mode === 'none' || !unit || unit <= 0) return value;

  switch (mode) {
    case 'nearest':
      return Math.round(value / unit) * unit;
    case 'up':
      return Math.ceil(value / unit) * unit;
    case 'down':
      return Math.floor(value / unit) * unit;
    default:
      return value;
  }
}

// ─── Row Target Matching ────────────────────────────────────────────

/**
 * Check if a data row matches a margin rule's target scope.
 * Returns true if the rule should apply to this row.
 */
export function rowMatchesTarget(row: ExportJobRow, rule: MarginRule): boolean {
  const scope = rule.target_scope as TargetScope;
  const config = rule.target_config || {};
  const targetValue = config.value != null ? String(config.value).trim() : '';

  switch (scope) {
    case 'all_rows':
      return true;

    case 'account_series':
      if (!targetValue) return true;
      return String(row.account_series || '').trim() === targetValue;

    case 'account_id':
      if (!targetValue) return true;
      return String(row.account_id || '').trim() === targetValue;

    case 'account_name':
      if (!targetValue) return true;
      return String(row.account_name || '').toLowerCase().includes(targetValue.toLowerCase());

    case 'project':
      if (!targetValue) return true;
      return String(row.project || '').toLowerCase() === targetValue.toLowerCase();

    case 'cost_code':
      if (!targetValue) return true;
      return String(row.tc_cost_code || '').toLowerCase() === targetValue.toLowerCase();

    default:
      return false;
  }
}

// ─── Single Margin Rule Application ─────────────────────────────────

/**
 * Apply a single margin rule to a base numeric value.
 * Returns the adjusted value before and after rounding.
 */
export function applyMarginToValue(
  baseValue: number,
  rule: MarginRule
): { adjustedPreRound: number; finalValue: number } {
  let adjusted = baseValue;

  switch (rule.margin_type) {
    case 'percentage':
      adjusted = baseValue * (1 + rule.margin_value / 100);
      break;
    case 'fixed_amount':
      adjusted = baseValue + rule.margin_value;
      break;
  }

  const finalValue = applyRounding(
    adjusted,
    rule.rounding_mode as RoundingMode,
    rule.rounding_unit
  );

  return { adjustedPreRound: adjusted, finalValue };
}

// ─── Resolve Margin for a Field + Row ───────────────────────────────

/**
 * Find the first matching active margin rule for a given field and row,
 * then apply it. Returns a MarginResult with full detail.
 *
 * Rules are evaluated in sort_order. First matching active rule wins.
 */
export function resolveMarginForField(
  fieldName: string,
  row: ExportJobRow,
  rules: MarginRule[]
): MarginResult {
  // Get the base numeric value from the row
  const rawValue = (row as any)[fieldName];
  const baseValue = rawValue == null ? 0 : Number(rawValue) || 0;

  // Sort rules by sort_order and find first matching active rule
  const sortedRules = [...rules]
    .filter(r => r.is_active && r.applies_to_field === fieldName)
    .sort((a, b) => a.sort_order - b.sort_order);

  for (const rule of sortedRules) {
    if (rowMatchesTarget(row, rule)) {
      const { adjustedPreRound, finalValue } = applyMarginToValue(baseValue, rule);
      return {
        baseValue,
        adjustedPreRound: Math.round(adjustedPreRound * 100) / 100,
        finalValue: Math.round(finalValue * 100) / 100,
        appliedRule: rule,
        wasAdjusted: true,
      };
    }
  }

  // No rule matched
  return {
    baseValue,
    adjustedPreRound: baseValue,
    finalValue: baseValue,
    appliedRule: null,
    wasAdjusted: false,
  };
}

// ─── Resolve Column Value with Margin Rules ─────────────────────────

/**
 * Enhanced version of resolveColumnValue that also applies margin rules.
 * For numeric source_field columns or calculated math columns, checks if any
 * margin rule targets the field and applies the adjustment.
 *
 * Accepts optional resolvedCalcColumns map so calculated columns that reference
 * other calculated columns can look up their already-resolved values.
 *
 * Returns both the display value and the MarginResult for UI indicators.
 */
export function resolveColumnValueWithMargins(
  col: ExportTemplateColumn,
  row: ExportJobRow,
  aliasRules: AddressAliasRule[],
  marginRules: MarginRule[],
  townClassifications?: Map<string, TownClassification>,
  resolvedCalcColumns?: Map<string, string>,
  rangeEndDate?: string
): { value: string; marginResult: MarginResult | null } {
  if (!col.is_enabled) return { value: '', marginResult: null };

  // For calculated columns, pass margin rules, town classifications, and
  // resolvedCalcColumns through so inter-column references resolve correctly
  let baseValue: string;
  if (col.source_type === 'calculated') {
    baseValue = resolveCalculatedValue(
      col.transform_config as CalculatedConfig | null,
      row, aliasRules, marginRules, townClassifications, resolvedCalcColumns
    );
  } else {
    baseValue = resolveColumnValue(col, row, aliasRules, townClassifications, rangeEndDate);
  }

  // Check if any margin rules target this column's source field
  if (marginRules.length === 0) return { value: baseValue, marginResult: null };

  // Determine the field name to check against margin rules
  let targetField: string | null = null;

  if (col.source_type === 'source_field' && col.source_field) {
    targetField = col.source_field;
  } else if (col.source_type === 'calculated') {
    // For calculated columns, check if the export_column_name matches a margin rule's applies_to_field
    // This allows margin rules to target calculated template columns by name
    const matchingRule = marginRules.find(r =>
      r.is_active && r.applies_to_field === col.export_column_name
    );
    if (matchingRule) {
      targetField = col.export_column_name;
    }
  }

  if (!targetField) return { value: baseValue, marginResult: null };

  // Check if there are active rules for this field
  const relevantRules = marginRules.filter(r => r.is_active && r.applies_to_field === targetField);
  if (relevantRules.length === 0) return { value: baseValue, marginResult: null };

  // For source_field: use the raw row value as the base
  // For calculated: parse the resolved string value as a number
  let numericBase: number;
  if (col.source_type === 'source_field' && col.source_field) {
    const rawVal = (row as any)[col.source_field];
    numericBase = rawVal == null ? 0 : Number(rawVal) || 0;
  } else {
    numericBase = parseFloat(baseValue) || 0;
  }

  // Find first matching rule and apply
  const sortedRules = [...relevantRules].sort((a, b) => a.sort_order - b.sort_order);
  for (const rule of sortedRules) {
    if (rowMatchesTarget(row, rule)) {
      const { adjustedPreRound, finalValue } = applyMarginToValue(numericBase, rule);
      const marginResult: MarginResult = {
        baseValue: numericBase,
        adjustedPreRound: Math.round(adjustedPreRound * 100) / 100,
        finalValue: Math.round(finalValue * 100) / 100,
        appliedRule: rule,
        wasAdjusted: true,
      };
      return {
        value: String(marginResult.finalValue),
        marginResult,
      };
    }
  }

  return { value: baseValue, marginResult: null };
}


/**
 * Build a human-readable summary of a margin rule.
 */
export function describeMarginRule(rule: MarginRule): string {
  const sign = rule.margin_value >= 0 ? '+' : '';
  const marginStr = rule.margin_type === 'percentage'
    ? `${sign}${rule.margin_value}%`
    : `${sign}$${rule.margin_value.toFixed(2)}`;

  let desc = `Apply ${marginStr} to ${rule.applies_to_field}`;

  if (rule.target_scope !== 'all_rows') {
    const targetVal = rule.target_config?.value || '?';
    desc += ` for ${rule.target_scope.replace('_', ' ')} = ${targetVal}`;
  }

  if (rule.rounding_mode !== 'none') {
    desc += `, ${rule.rounding_mode} to $${rule.rounding_unit}`;
  }

  return desc;
}



// ═══════════════════════════════════════════════════════════════════════
// Part 6: Invoice Assignment Logic
// ═══════════════════════════════════════════════════════════════════════

// ─── Derive Invoice Passenger Name ──────────────────────────────────

/**
 * Derive the best available passenger identity from a row.
 * Uses combined name first, falls back to first+last, then individual fields.
 * Trims and normalizes for consistent grouping.
 */
export function deriveInvoicePassengerName(row: ExportJobRow): string {
  // Try passenger_name (combined) first
  if (row.passenger_name && row.passenger_name.trim()) {
    return row.passenger_name.trim();
  }

  // Try first + last name
  const first = (row.passenger_first_name || '').trim();
  const last = (row.passenger_last_name || '').trim();
  if (first || last) {
    return [first, last].filter(Boolean).join(' ').trim();
  }

  // Fallback: empty (will group as "Unknown Passenger")
  return '';
}

// ─── Build Invoice Group Key ────────────────────────────────────────

/**
 * Build the invoice group key for a row based on the grouping mode.
 * Rows with the same group key will share the same invoice number.
 *
 * Uses a stable delimiter (||) to join key parts.
 */
export function buildInvoiceGroupKey(
  row: ExportJobRow,
  settings: InvoiceRunSettings
): string {
  const DELIM = '||';
  const normalize = (val: any): string => {
    if (val == null || val === '') return '__EMPTY__';
    return String(val).trim().toLowerCase();
  };

  switch (settings.groupingMode) {
    case 'account_only':
      return normalize(row.account_id);

    case 'account_plus_passenger': {
      const passenger = deriveInvoicePassengerName(row) || '__UNKNOWN__';
      return [normalize(row.account_id), normalize(passenger)].join(DELIM);
    }

    case 'account_plus_reference':
      return [normalize(row.account_id), normalize(row.reference)].join(DELIM);

    case 'custom_fields': {
      const fields = settings.customGroupFields;
      if (fields.length === 0) return normalize(row.account_id);
      return fields.map(f => {
        if (f === 'invoice_passenger_name') {
          return normalize(deriveInvoicePassengerName(row));
        }
        return normalize((row as any)[f]);
      }).join(DELIM);
    }

    default:
      return normalize(row.account_id);
  }
}

// ─── Assign Invoice Numbers ─────────────────────────────────────────

/**
 * Assign sequential invoice numbers to a set of rows.
 *
 * Process:
 * 1. Derive group key for each row
 * 2. Determine unique groups in order of first appearance
 * 3. Assign sequential invoice numbers starting from startingInvoiceNumber
 * 4. Return per-row assignments and a summary
 *
 * @param rows - The current filtered dataset (in display order)
 * @param settings - Invoice run settings (starting number, grouping mode, custom fields)
 * @returns Object containing assignments map and summary
 */
export function assignInvoiceNumbers(
  rows: ExportJobRow[],
  settings: InvoiceRunSettings
): {
  assignments: Map<string, InvoiceAssignment>;
  summary: InvoiceRunSummary;
} {
  const assignments = new Map<string, InvoiceAssignment>();

  // Track unique groups in order of first appearance
  const groupOrder: string[] = [];
  const groupInvoiceMap = new Map<string, number>();
  const groupRowCounts = new Map<string, number>();
  const groupAccountIds = new Map<string, string | null>();
  const groupPassengerNames = new Map<string, string | null>();

  let nextInvoiceNumber = settings.startingInvoiceNumber;

  for (const row of rows) {
    const passengerName = deriveInvoicePassengerName(row);
    const groupKey = buildInvoiceGroupKey(row, settings);

    // Assign invoice number to group if not yet assigned
    if (!groupInvoiceMap.has(groupKey)) {
      groupInvoiceMap.set(groupKey, nextInvoiceNumber);
      groupOrder.push(groupKey);
      groupRowCounts.set(groupKey, 0);
      groupAccountIds.set(groupKey, row.account_id);
      groupPassengerNames.set(groupKey, passengerName || null);
      nextInvoiceNumber++;
    }

    const invoiceNumber = groupInvoiceMap.get(groupKey)!;
    groupRowCounts.set(groupKey, (groupRowCounts.get(groupKey) || 0) + 1);

    assignments.set(row.tc_id, {
      rowId: row.tc_id,
      invoicePassengerName: passengerName,
      invoiceGroupKey: groupKey,
      assignedInvoiceNumber: invoiceNumber,
    });
  }

  // Build summary
  const uniqueGroups = groupOrder.length;
  const endingNumber = uniqueGroups > 0 ? settings.startingInvoiceNumber + uniqueGroups - 1 : settings.startingInvoiceNumber;

  const groupBreakdown = groupOrder.map(gk => ({
    invoiceNumber: groupInvoiceMap.get(gk)!,
    groupKey: gk,
    rowCount: groupRowCounts.get(gk) || 0,
    accountId: groupAccountIds.get(gk) || null,
    passengerName: groupPassengerNames.get(gk) || null,
  }));

  const summary: InvoiceRunSummary = {
    startingNumber: settings.startingInvoiceNumber,
    endingNumber,
    uniqueGroups,
    totalRows: rows.length,
    groupBreakdown,
  };

  return { assignments, summary };
}

/**
 * Augment a row with invoice-derived fields for use in column resolution.
 * Returns a new row object with invoice_passenger_name, invoice_group_key,
 * and assigned_invoice_number fields added.
 */
export function augmentRowWithInvoiceData(
  row: ExportJobRow,
  assignment: InvoiceAssignment | undefined
): ExportJobRow {
  if (!assignment) return row;

  return {
    ...row,
    invoice_passenger_name: assignment.invoicePassengerName,
    invoice_group_key: assignment.invoiceGroupKey,
    assigned_invoice_number: assignment.assignedInvoiceNumber,
  } as any;
}
